import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

// Small nav-badge counts — "how many things need your attention right now" next to the
// Calendar/Course links in the sidebar. Deliberately count-only (head: true) queries, not
// full row fetches, so this stays cheap enough to run on every page load inside AppShell.

/** Upcoming scheduled lessons + pending booking requests, for whichever side of the
 * relationship the caller is on. Both count as "something on your calendar." */
function useCalendarBadge(idColumn: 'instructor_id' | 'student_id') {
  const { user } = useAuth()
  const [count, setCount] = useState(0)

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return }
    const nowIso = new Date().toISOString()
    const [lessonsRes, requestsRes] = await Promise.all([
      supabase.from('driving_lessons').select('id', { count: 'exact', head: true })
        .eq(idColumn, user.id).eq('status', 'scheduled').gte('scheduled_at', nowIso),
      supabase.from('lesson_booking_requests').select('id', { count: 'exact', head: true })
        .eq(idColumn, user.id).eq('status', 'pending'),
    ])
    setCount((lessonsRes.count ?? 0) + (requestsRes.count ?? 0))
  }, [user, idColumn])

  useEffect(() => { refresh() }, [refresh])
  return count
}

export function useInstructorCalendarBadge() {
  return useCalendarBadge('instructor_id')
}

export function useStudentCalendarBadge() {
  return useCalendarBadge('student_id')
}

/** Assigned curriculum lessons the student hasn't completed the in-car portion of yet --
 * one count per LESSON (a PDF+video pair), not per asset. */
export function useStudentCourseBadge() {
  const { user } = useAuth()
  const [count, setCount] = useState(0)

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return }
    const { count: n } = await supabase
      .from('curriculum_lesson_assignments')
      .select('id', { count: 'exact', head: true })
      .eq('student_id', user.id)
      .is('in_car_completed_at', null)
    setCount(n ?? 0)
  }, [user])

  useEffect(() => { refresh() }, [refresh])
  return count
}
